import { useCallback } from 'react';
import { useErrorStore } from '@/src/services/stores/error.store';
import { useRequestStore } from '@/src/services/stores/request.store';
import { ApiError } from '@/src/types/api.types';

/**
 * Hook to check error state and clear errors
 */
export const useApiError = () => {
  const { errors, globalError, setError, setGlobalError, clearError, getError, hasError } = useErrorStore();

  return {
    errors,
    globalError,
    setError,
    setGlobalError,
    clearError,
    getError,
    hasError,
  };
};

/**
 * Hook to check loading state for specific requests
 */
export const useApiLoading = () => {
  const { loadingStates, setLoading, isLoading, clearLoading } = useRequestStore();

  return {
    loadingStates,
    setLoading,
    isLoading,
    clearLoading,
  };
};

/**
 * Hook for checking if a specific API call is loading
 */
export const useIsLoading = (endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET'): boolean => {
  const { isLoading } = useRequestStore();
  const requestKey = `${method}_${endpoint}`;
  return isLoading(requestKey);
};

/**
 * Hook for getting error for a specific endpoint
 */
export const useEndpointError = (endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET'): ApiError | undefined => {
  const { getError } = useErrorStore();
  const requestKey = `${method}_${endpoint}`;
  return getError(requestKey);
};

/**
 * Hook for handling request lifecycle (useful for forms and API calls)
 */
export const useRequestLifecycle = (endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST') => {
  const requestStore = useRequestStore();
  const errorStore = useErrorStore();
  const requestKey = `${method}_${endpoint}`;

  const setLoading = useCallback(
    (loading: boolean) => {
      requestStore.setLoading(requestKey, loading);
    },
    [requestKey, requestStore]
  );

  const setError = useCallback(
    (error: ApiError | null) => {
      if (error) {
        errorStore.setError(requestKey, error);
      } else {
        errorStore.clearError(requestKey);
      }
    },
    [requestKey, errorStore]
  );

  const isLoading = requestStore.isLoading(requestKey);
  const error = errorStore.getError(requestKey);

  return {
    isLoading,
    error,
    setLoading,
    setError,
    requestKey,
  };
};
